// Per-agent run limits edited in the hosted-agent wizard's Model tab.
// Mirrors `AgentRuntimeLimits` in services/hosted_agents/models.py; the form
// keeps each limit as an input string where "" means "use the server default".

export type HostedAgentRuntimeBounds = {
  readonly default: number
  readonly min: number
  readonly max: number
}

/** `runtime.max_iterations` (#3033): default 40, ge=1, le=200. */
export const HOSTED_AGENT_MAX_ITERATIONS = { default: 40, min: 1, max: 200 } as const

/**
 * `runtime.max_web_tool_calls` (#3334): default 30, ge=1, le=200. Web lookups
 * past it are refused and the agent answers with what it has.
 */
export const HOSTED_AGENT_MAX_WEB_TOOL_CALLS = { default: 30, min: 1, max: 200 } as const

/**
 * `runtime.max_run_seconds` (#3334): default 600, ge=60, le=3600. Wall clock
 * per run; past it the run is ended with its partial answer. Edited in
 * seconds, like the stored value.
 */
export const HOSTED_AGENT_MAX_RUN_SECONDS = { default: 600, min: 60, max: 3600 } as const

/** The runtime limits as form input strings; "" = not set (server default). */
export type HostedAgentRuntimeLimitInputs = {
  runtimeMaxIterations: string
  runtimeMaxWebToolCalls: string
  runtimeMaxRunSeconds: string
}

const RUNTIME_LIMIT_FIELDS: ReadonlyArray<{
  key: "max_iterations" | "max_web_tool_calls" | "max_run_seconds"
  field: keyof HostedAgentRuntimeLimitInputs
  bounds: HostedAgentRuntimeBounds
}> = [
  { key: "max_iterations", field: "runtimeMaxIterations", bounds: HOSTED_AGENT_MAX_ITERATIONS },
  {
    key: "max_web_tool_calls",
    field: "runtimeMaxWebToolCalls",
    bounds: HOSTED_AGENT_MAX_WEB_TOOL_CALLS,
  },
  { key: "max_run_seconds", field: "runtimeMaxRunSeconds", bounds: HOSTED_AGENT_MAX_RUN_SECONDS },
]

/**
 * Parse an optional runtime-limit input: null for "" (server default), the
 * integer when within [min, max], otherwise undefined (invalid).
 */
export function parseHostedAgentRuntimeLimit(
  value: string,
  bounds: HostedAgentRuntimeBounds
): number | null | undefined {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed.replace(/\s+/g, ""))
  if (!Number.isInteger(parsed)) return undefined
  if (parsed < bounds.min || parsed > bounds.max) return undefined
  return parsed
}

/** `parseHostedAgentRuntimeLimit` for `max_iterations`. */
export function parseHostedAgentMaxIterations(value: string): number | null | undefined {
  return parseHostedAgentRuntimeLimit(value, HOSTED_AGENT_MAX_ITERATIONS)
}

/** The stored runtime limits as form inputs; a missing or non-number value reads as "". */
export function readHostedAgentRuntimeLimits(
  runtime: Readonly<Record<string, unknown>>
): HostedAgentRuntimeLimitInputs {
  const inputs = {} as HostedAgentRuntimeLimitInputs
  for (const { key, field } of RUNTIME_LIMIT_FIELDS) {
    const value = runtime[key]
    inputs[field] = typeof value === "number" ? String(value) : ""
  }
  return inputs
}

/** The form fields whose runtime-limit input is outside the backend's range. */
export function invalidHostedAgentRuntimeLimits(
  inputs: HostedAgentRuntimeLimitInputs
): Array<keyof HostedAgentRuntimeLimitInputs> {
  return RUNTIME_LIMIT_FIELDS.filter(
    ({ field, bounds }) => parseHostedAgentRuntimeLimit(inputs[field], bounds) === undefined
  ).map(({ field }) => field)
}

/**
 * The runtime block to save: the stored document with the editable limits
 * replaced. mem_limit/pids_limit and any key this client does not know ride
 * along untouched; "" drops the key so the server default applies — never
 * 0 or null.
 */
export function applyHostedAgentRuntimeLimits(
  runtime: Readonly<Record<string, unknown>>,
  inputs: HostedAgentRuntimeLimitInputs
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...runtime }
  for (const { key, field, bounds } of RUNTIME_LIMIT_FIELDS) {
    delete next[key]
    const parsed = parseHostedAgentRuntimeLimit(inputs[field], bounds)
    if (typeof parsed === "number") next[key] = parsed
  }
  return next
}
